import React, { useState } from 'react';
import { Drawer, Button } from 'components/common';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FaFileImport, FaDownload } from 'react-icons/fa';
import { MdErrorOutline, MdOutlineDoneAll } from 'react-icons/md';
import { importPackingList, downloadTemplatePackingList } from 'redux/crm/shipment/packingListSlice';
const COMMON_SHEETS = ['PKL VTF', 'PKL OS'];

// Tailwind drawer replacement for the old full-page ImportPackingList.
const ImportPackingListDrawer = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [selectedSheets, setSelectedSheets] = useState(['PKL VTF']);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');

  const resetState = () => {
    setFile(null);
    setSelectedSheets(['PKL VTF']);
    setImportResult(null);
    setError('');
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    const valid =
      selected.name.endsWith('.xlsx') || selected.name.endsWith('.xls');
    if (!valid) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      setFile(null);
      return;
    }
    setError('');
    setFile(selected);
  };

  const toggleSheet = (sheet) => {
    setSelectedSheets((prev) =>
      prev.includes(sheet) ? prev.filter((s) => s !== sheet) : [...prev, sheet]
    );
  };

  const handleDownloadTemplate = async () => {
    await dispatch(downloadTemplatePackingList('packing-list'));
  };

  const handleImport = async () => {
    if (!file) { setError('Please select a file to import'); return; }
    if (selectedSheets.length === 0) { setError('Please select at least one sheet to import'); return; }

    setLoading(true);
    setError('');
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('sheets', JSON.stringify(selectedSheets));

    try {
      const response = await dispatch(importPackingList(formData)).unwrap();
      if (response.success) {
        setImportResult(response.data);
      } else {
        setError(response.message || 'Import failed');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred during import. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const successRate = importResult && importResult.total_rows > 0
    ? ((importResult.successful_records / importResult.total_rows) * 100).toFixed(1)
    : 0;

  const footer = (
    <div className="tw-flex tw-justify-end tw-gap-2 tw-w-full">
      {importResult ? (
        <>
          <Button variant="ghost" size="sm" onClick={handleClose}>Close</Button>
          <Button variant="primary" size="sm" onClick={resetState}>
            <FaFileImport size={12} /> Import another file
          </Button>
        </>
      ) : (
        <>
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleImport} disabled={!file || selectedSheets.length === 0 || loading}>
            <FaFileImport size={12} /> {loading ? 'Importing…' : 'Import now'}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Drawer
      show={show}
      onHide={handleClose}
      backdropClick={false}
      width="560px"
      minWidth="420px"
      title={<span className="tw-flex tw-items-center tw-gap-2"><FaFileImport size={14} className="tw-text-blue-500" /> Import Packing List</span>}
      footer={footer}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {error && (
          <div className="tw-flex tw-items-center tw-gap-2 tw-bg-red-50 tw-border tw-border-red-200 tw-text-red-700 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm">
            <MdErrorOutline size={18} /> {error}
          </div>
        )}

        {!importResult ? (
          <>
            {/* Step 1 — file */}
            <div className="tw-rounded-lg tw-border-2 tw-border-dashed tw-border-blue-300 tw-bg-blue-50/50 tw-p-4">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <h5 className="tw-mb-0 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-slate-700">
                  <span className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-text-xs">1</span>
                  Select Excel file
                </h5>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-blue-600 hover:tw-text-blue-800 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                >
                  <FaDownload size={11} /> Download template
                </button>
              </div>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
                className="tw-block tw-w-full tw-text-sm tw-text-slate-600 file:tw-mr-3 file:tw-py-2 file:tw-px-4 file:tw-rounded-md file:tw-border-0 file:tw-bg-blue-100 file:tw-text-blue-700 file:tw-font-semibold hover:file:tw-bg-blue-200 tw-cursor-pointer"
              />
              {file && (
                <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-text-emerald-600 tw-font-semibold">✓ {file.name}</p>
              )}
              <p className="tw-mt-1 tw-mb-0 tw-text-xs tw-text-slate-400">Supported: .xlsx, .xls • Max 10MB</p>
            </div>

            {/* Step 2 — sheets */}
            {file && (
              <div className="tw-rounded-lg tw-border-2 tw-border-dashed tw-border-primary-300 tw-bg-primary-50/50 tw-p-4">
                <h5 className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-slate-700">
                  <span className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-xl tw-bg-primary-500 tw-text-white tw-text-xs">2</span>
                  Select sheets to import
                </h5>
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  {COMMON_SHEETS.map((sheet) => (
                    <label
                      key={sheet}
                      className={`tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-md tw-border tw-cursor-pointer tw-text-sm tw-transition-colors ${
                        selectedSheets.includes(sheet)
                          ? 'tw-bg-blue-100 tw-border-blue-400 tw-text-blue-700'
                          : 'tw-bg-white tw-border-slate-300 tw-text-slate-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSheets.includes(sheet)}
                        onChange={() => toggleSheet(sheet)}
                        disabled={loading}
                        className="tw-w-4 tw-h-4 tw-accent-blue-600"
                      />
                      {sheet}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {loading && (
              <div className="tw-h-1.5 tw-w-full tw-rounded-xl tw-bg-slate-200 tw-overflow-hidden">
                <div className="tw-h-full tw-w-1/2 tw-bg-blue-500 tw-animate-pulse" />
              </div>
            )}
          </>
        ) : (
          /* Result summary */
          <div className="tw-flex tw-flex-col tw-gap-4">
            <h5 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-slate-700">Import Summary</h5>
            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
              {[
                { label: 'Total Rows', value: importResult.total_rows, cls: 'tw-text-blue-600 tw-bg-blue-50 tw-border-blue-200' },
                { label: 'Successful', value: importResult.successful_records, cls: 'tw-text-emerald-600 tw-bg-emerald-50 tw-border-emerald-200' },
                { label: 'Processed', value: importResult.imported, cls: 'tw-text-amber-600 tw-bg-amber-50 tw-border-amber-200' },
                { label: 'Failed', value: (importResult.errors || 0) + (importResult.failed_records || 0), cls: 'tw-text-red-600 tw-bg-red-50 tw-border-red-200' },
              ].map(({ label, value, cls }) => (
                <div key={label} className={`tw-rounded tw-border tw-p-3 ${cls}`}>
                  <p className="tw-text-xs tw-text-slate-500 tw-mb-1">{label}</p>
                  <p className="tw-text-xl tw-font-bold tw-mb-0">{value ?? 0}</p>
                </div>
              ))}
            </div>

            <div className="tw-bg-slate-50 tw-rounded tw-p-3">
              <div className="tw-flex tw-justify-between tw-text-sm tw-mb-2">
                <span className="tw-font-semibold tw-text-slate-700">Success Rate</span>
                <span className="tw-font-bold tw-text-blue-600">{successRate}%</span>
              </div>
              <div className="tw-h-2 tw-w-full tw-rounded-xl tw-bg-slate-200 tw-overflow-hidden">
                <div
                  className={`tw-h-full tw-rounded-xl ${successRate >= 80 ? 'tw-bg-emerald-500' : successRate >= 50 ? 'tw-bg-amber-500' : 'tw-bg-red-500'}`}
                  style={{ width: `${successRate}%` }}
                />
              </div>
            </div>

            {importResult.failed_record_details?.length > 0 && (
              <div>
                <h6 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-slate-700 tw-mb-2">
                  <MdErrorOutline className="tw-text-amber-500" size={16} /> Validation Errors
                </h6>
                <div className="tw-overflow-x-auto tw-border tw-border-slate-200 tw-rounded">
                  <table className="tw-w-full tw-text-xs">
                    <thead className="tw-bg-slate-50 tw-text-slate-500">
                      <tr>
                        <th className="tw-text-left tw-px-2 tw-py-1.5">#</th>
                        <th className="tw-text-left tw-px-2 tw-py-1.5">Job Number</th>
                        <th className="tw-text-left tw-px-2 tw-py-1.5">Packing No</th>
                        <th className="tw-text-left tw-px-2 tw-py-1.5">Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.failed_record_details.map((rec, i) => (
                        <tr key={i} className="tw-border-t tw-border-slate-100">
                          <td className="tw-px-2 tw-py-1.5">{rec.index}</td>
                          <td className="tw-px-2 tw-py-1.5 tw-text-red-600 tw-font-mono">{rec.job_number}</td>
                          <td className="tw-px-2 tw-py-1.5 tw-text-red-600 tw-font-mono">{rec.packing_no}</td>
                          <td className="tw-px-2 tw-py-1.5">
                            <ul className="tw-mb-0 tw-pl-4 tw-list-disc">
                              {rec.errors.map((err, j) => (
                                <li key={j} className="tw-text-red-600">{err}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {importResult.error_details?.length > 0 && (
              <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-3">
                <h6 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-red-700 tw-mb-2">
                  <MdErrorOutline size={16} /> Processing Errors
                </h6>
                <ul className="tw-mb-0 tw-pl-4 tw-list-disc tw-text-xs tw-text-red-600">
                  {importResult.error_details.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}

            {!importResult.failed_record_details?.length && !importResult.error_details?.length && (
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-emerald-600 tw-text-sm tw-font-semibold">
                <MdOutlineDoneAll size={18} /> All records imported successfully.
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

ImportPackingListDrawer.propTypes = {
  show:   PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default ImportPackingListDrawer;
